import React, { ReactElement, useState } from 'react';
import './CreateJobFieldList.scss';
import { CallFieldMappingConfig, CallLogData } from '../../store/state/createJobState';
import { MappingRequirementType } from '../../models/mappingRequirementType';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store/actions/appDispatch';
import { clearCallFieldMapping, updateCallFieldMappingConfig } from '../../store/actions/createJobActions';
import {
	getCallFieldMappingResult,
	getColumnIndexesForMappingConfig,
	getErrorCallFieldValidations,
	getWarningCallFieldValidations,
	getCallFieldMappingValidationResultsByMappingId, 
	CallFieldMappingResult
} from '../../store/selectors/createJobSelectors';
import MappingConfigurationModal, { MappingConfigurationModalData } from './MappingConfigurationModal';
import Tooltip from '../Tooltip';
import errorIcon from '../../imgs/icons/error.svg';
import warningIcon from '../../imgs/icons/warning.svg';
import CreateJobDirectionSummary from './CreateJobDirectionSummary';
import MappingResultPreview from '../MappingResultPreview';
import CreateJobParticipantNameSummary from './CreateJobParticipantNameSummary';

const noColumnSelectedValue = '____empty____';

export default function CreateJobFieldList(props: Props) {
	const dispatch = useDispatch<AppDispatch>();
	const [previewRow, setPreviewRow] = useState<number>(1);
	const [configData, setConfigData] = useState<MappingConfigurationModalData | null>(null);
	const groupings = getMappingsGroupedByRequirementType(props.mappings);
	const validationResults = getCallFieldMappingValidationResultsByMappingId(props.mappings, props.callLogData);
	const selectedColumnIndexes = getSelectedColumnIndexes();
	
	if (!props.callLogData.headers.length || !props.callLogData.callRows.length)
		return <div className="text-center text-italic">Data is missing or invalid</div>;
	
	const headers = props.callLogData.headers;
	
	return (
		<React.Fragment>
			<div className="create-job-field-list__container">
				<div className="create-job-field-list__main-header">
					<div className="create-job-field-list__field-description-column-item"><span className="text-bold">Creovai field</span></div>
					<div className="create-job-field-list__field-mapping-column-item"><span className="text-bold">Call log field</span></div>
					<div className="create-job-field-list__field-preview-column-item">
						<div className="create-job-field-list__preview-column-label">Preview from row {previewRow.toLocaleString()}</div>
						<button className="button-box button-box_small mr-2" onClick={() => setPreviewRow(prev => prev - 1)} disabled={previewRow === 1}><span className="button-box-caret-down"></span></button>
						<button className="button-box button-box_small" onClick={() => setPreviewRow(prev => prev + 1)} disabled={previewRow === props.callLogData.callRows.length}><span className="button-box-caret-up"></span></button>
					</div>
				</div>

				{groupings.filter(g => g.mappings.length > 0).map(g => (
					<div key={g.requirementType} className="create-job-field-list__group">
						<div className="create-job-field-list__group-header">
							<div className="create-job-field-list__field-description-column-item">
								<span className={classNames('text-bold', {'text-red': g.requirementType === MappingRequirementType.Required})}>{g.requirementType}</span>
							</div>
						</div>

						{g.mappings.map(mapping => {
							const validationResult = validationResults.get(mapping.id);
							const errors = validationResult ? getErrorCallFieldValidations(validationResult, props.showNonIntrusiveErrors).map(v => v.message) : [];
							const warnings = validationResult ? getWarningCallFieldValidations(validationResult).map(v => v.message) : [];
							const previewResult = getPreviewResult(mapping);
							const mappingConst = mapping;
							
							return (
								<div className="create-job-field-list__field-row" key={mapping.id}>
									<div className="create-job-field-list__field-description-column-item">
										<div className="create-job-field-list__field-description-name">
											{mapping.displayName}
											{!!errors.length && <Tooltip renderContent={() => renderValidationTooltipContent(errors)} placement="bottom" fallbackPlacements={['bottom-start', 'bottom-end']}><img src={errorIcon} tabIndex={0} aria-label={errors.join(', ')} alt="Error" className="create-job-field-list__error-icon pretty-focus" /></Tooltip>}
											{!!warnings.length && <Tooltip renderContent={() => renderValidationTooltipContent(warnings)} placement="bottom" fallbackPlacements={['bottom-start', 'bottom-end']}><img src={warningIcon} tabIndex={0} aria-label={warnings.join(', ')} alt="Warning" className="create-job-field-list__error-icon pretty-focus" /></Tooltip>}
										</div>
										{!!mapping.helpText &&
											<div className="create-job-field-list__field-help-text">{mapping.helpText}</div>
										}
									</div>
									<div className="create-job-field-list__field-mapping-column-item">
										{mappingConst.type !== 'direction' && mappingConst.type !== 'participantName' &&
										<select className="create-job-field-list__field-mapping-select" 
												value={mappingConst.columnIndex?.toString() ?? noColumnSelectedValue} 
												placeholder="No selection"
												aria-label={`Choose call log field for ${mapping.displayName}`}
												onChange={e => handleFieldMapped(mapping, e.target.value)}>
											<option value={noColumnSelectedValue}>No selection</option>
											{headers.map((header, i) => (
												<option value={i.toString()} key={i} disabled={selectedColumnIndexes.has(i) && mappingConst.columnIndex !== i}>{header}</option>
											))}
										</select>
										}

										{mappingConst.type === 'direction' && 
											<React.Fragment>
												<CreateJobDirectionSummary mapping={mapping} 
																		   callLogData={props.callLogData} 
																		   onEditConfiguration={() => openMappingConfigurationModal(mapping)} 
																		   onRemoveConfiguration={() => dispatch(clearCallFieldMapping(props.engagementId, mapping.id))} />
											</React.Fragment>
										}

										{mappingConst.type === 'date' && mappingConst.columnIndex != null &&
											<div className="mt-2">
												<a role="button" onClick={() => openMappingConfigurationModal(mapping, mappingConst.columnIndex)}>Configure</a>
											</div>
										}
										
										{mappingConst.type === 'participantName' &&
											<React.Fragment>
												<CreateJobParticipantNameSummary config={mappingConst} 
																				 callLogData={props.callLogData}
																				 onEditConfiguration={() => openMappingConfigurationModal(mapping)}
																				 onRemoveConfiguration={() => dispatch(clearCallFieldMapping(props.engagementId, mapping.id))} />
											</React.Fragment>
										}
									</div>
									<div className="create-job-field-list__field-preview-column-item">
										{!!previewResult && <MappingResultPreview mappingResult={previewResult} separator="line" />}
									</div>
								</div>
							);
						})}
					</div>
				))}
			</div>
			
			{!!configData && 
				<React.Fragment>
					<MappingConfigurationModal configurationData={configData} 
											   mappings={props.mappings}
											   engagementId={props.engagementId} 
											   callLogData={props.callLogData} 
											   onDone={() => setConfigData(null)} />
				</React.Fragment>
			}
		</React.Fragment>
	);
	
	function renderValidationTooltipContent(messages: string[]): ReactElement {
		return (
			<React.Fragment>
				{messages.map((m, i) => (<div key={i}>{m}</div>))}
			</React.Fragment>
		)
	}
	
	function handleFieldMapped(mapping: CallFieldMappingConfig, columnIndex: string): void {
		if (columnIndex === noColumnSelectedValue) {
			dispatch(clearCallFieldMapping(props.engagementId, mapping.id));
		} else {
			const parsed = parseInt(columnIndex);
			if (!isNaN(parsed)) {
				switch (mapping.type) {
					case 'date':
						openMappingConfigurationModal(mapping, parsed);
						break;
					case 'participantName':
					case 'direction':
						// these don't apply - for these we show a 'configure'
						// link - not the select control
						break;
					default:
						dispatch(updateCallFieldMappingConfig(props.engagementId, { ...mapping, columnIndex: parsed }));
						break;
				}
			}
		}
	}
	
	function openMappingConfigurationModal(mapping: CallFieldMappingConfig, columnIndex?: number | null): void {
		switch (mapping.type) {
			case 'date':
				if (columnIndex != null) {
					setConfigData({
						type: 'date',
						config: mapping,
						logColumnIndex: columnIndex
					});
				}
				break;
			case 'direction':
				setConfigData({
					type: 'direction',
					config: mapping
				});
				break;
			case 'participantName':
				setConfigData({
					type: 'participantName',
					config: mapping
				});
				break;
		}
	}
	
	function getPreviewResult(mapping: CallFieldMappingConfig): CallFieldMappingResult | null {
		const row = props.callLogData.callRows[previewRow - 1];
		if (!row) return null;
		
		return getCallFieldMappingResult(row, mapping);
	}
	
	function getSelectedColumnIndexes(): Set<number> {
		const result: Set<number> = new Set();
		
		props.mappings.forEach(m => {
			getColumnIndexesForMappingConfig(m).forEach(i => {
				result.add(i);
			});
		});
		
		return result;
	}
}

function getMappingsGroupedByRequirementType(mappings: CallFieldMappingConfig[]): { requirementType: MappingRequirementType; mappings: CallFieldMappingConfig[]; }[] {
	return [MappingRequirementType.Required, MappingRequirementType.Recommended, MappingRequirementType.Optional].map(type => {
		return { requirementType: type, mappings: mappings.filter(m => m.requirementType === type) };
	});
}

interface Props {
	engagementId: number;
	callLogData: CallLogData;
	mappings: CallFieldMappingConfig[];
	showNonIntrusiveErrors: boolean;
}
